import { useState, useMemo, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import SASidebar from '@/pages/sa/dashboard/components/SASidebar';
import { radioCommunityStats, communityMetrics, type RadioCommunityStats } from '@/mocks/communityCenter';

type LoadingState = 'loading' | 'ready' | 'error';

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function simulateLoad<T>(data: T): Promise<T> {
  return new Promise((resolve, reject) => {
    const d = 600 + Math.random() * 400;
    setTimeout(() => {
      if (Math.random() > 0.96) {
        reject(new Error('Timeout al conectar con el servidor.'));
      } else {
        resolve(data);
      }
    }, d);
  });
}

/* ── Community Score Gauge ── */
function ScoreGauge({ score, size }: { score: number; size?: 'sm' | 'md' }) {
  const isSm = size === 'sm';
  const dims = isSm ? 40 : 56;
  const strokeW = isSm ? 4 : 5;
  const radius = (dims - strokeW) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;

  const color = score >= 90 ? '#059669' : score >= 80 ? '#2563eb' : score >= 70 ? '#d97706' : '#6b7280';

  return (
    <div className="relative inline-flex items-center justify-center shrink-0" style={{ width: dims, height: dims }}>
      <svg width={dims} height={dims} className="-rotate-90">
        <circle
          cx={dims / 2}
          cy={dims / 2}
          r={radius}
          fill="none"
          stroke="oklch(var(--background-200))"
          strokeWidth={strokeW}
        />
        <circle
          cx={dims / 2}
          cy={dims / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeW}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className="transition-all duration-700 ease-out"
        />
      </svg>
      <span className={`absolute font-bold text-foreground-800 ${isSm ? 'text-[10px]' : 'text-xs'}`}>{score}</span>
    </div>
  );
}

/* ── KPI Card ── */
function KpiCard({ label, value, icon, bg, color, pulse }: {
  label: string; value: string; icon: string; bg: string; color: string; pulse?: boolean;
}) {
  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl p-4 flex items-center gap-3 hover:border-background-300 transition-colors relative overflow-hidden">
      {pulse && (
        <span className="absolute top-2 right-2 flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
        </span>
      )}
      <div className={`w-10 h-10 rounded-xl ${bg} flex items-center justify-center shrink-0`}>
        <i className={`${icon} ${color} text-lg`}></i>
      </div>
      <div className="min-w-0">
        <p className="text-foreground-400 text-[10px] font-medium truncate">{label}</p>
        <p className="text-foreground-800 text-lg font-bold leading-tight">{value}</p>
      </div>
    </div>
  );
}

/* ── Radio Community Card ── */
function RadioCommunityCard({ radio, rank }: { radio: RadioCommunityStats; rank: number }) {
  const scoreColor = radio.communityScore >= 90 ? 'text-emerald-600 bg-emerald-50' : radio.communityScore >= 80 ? 'text-sky-600 bg-sky-50' : radio.communityScore >= 70 ? 'text-amber-600 bg-amber-50' : 'text-foreground-500 bg-foreground-100';

  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl p-4 hover:border-background-300 transition-all group space-y-3">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="relative">
            <div className="w-10 h-10 rounded-xl bg-background-100 border border-background-200 flex items-center justify-center shrink-0">
              <i className="ri-radio-line text-foreground-500 text-sm"></i>
            </div>
            {rank <= 3 && (
              <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-accent-500 border-2 border-background-50 flex items-center justify-center">
                <span className="text-background-50 text-[8px] font-bold">{rank}</span>
              </span>
            )}
          </div>
          <div className="min-w-0">
            <p className="text-foreground-800 text-xs font-semibold line-clamp-1">{radio.radioName}</p>
            <p className="text-foreground-400 text-[9px] mt-0.5">{radio.radioFrequency} · {radio.radioCity}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {radio.trending && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent-50 text-accent-600 text-[9px] font-medium whitespace-nowrap">
              <i className="ri-fire-line text-[9px]"></i>
              Trending
            </span>
          )}
          <ScoreGauge score={radio.communityScore} size="sm" />
        </div>
      </div>

      {/* Community Score Bar */}
      <div className="space-y-1">
        <div className="flex items-center justify-between text-[9px]">
          <span className="text-foreground-400">Community Score</span>
          <span className={`font-bold ${radio.communityScore >= 90 ? 'text-emerald-600' : radio.communityScore >= 80 ? 'text-sky-600' : radio.communityScore >= 70 ? 'text-amber-600' : 'text-foreground-500'}`}>
            {radio.communityScore >= 90 ? 'Excelente' : radio.communityScore >= 80 ? 'Muy Bueno' : radio.communityScore >= 70 ? 'Bueno' : 'Moderado'}
          </span>
        </div>
        <div className="w-full h-1.5 bg-background-200 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-700 ${
              radio.communityScore >= 90 ? 'bg-emerald-500' : radio.communityScore >= 80 ? 'bg-sky-500' : radio.communityScore >= 70 ? 'bg-amber-500' : 'bg-foreground-400'
            }`}
            style={{ width: `${radio.communityScore}%` }}
          ></div>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-6 gap-1.5">
        <div className="text-center bg-background-100 rounded-lg py-1.5 px-1">
          <p className="text-foreground-800 text-[11px] font-bold">{radio.activeHosts}</p>
          <p className="text-foreground-400 text-[7px] flex items-center justify-center gap-0.5 whitespace-nowrap">
            <i className="ri-mic-line text-[7px]"></i>
            Hosts
          </p>
        </div>
        <div className="text-center bg-background-100 rounded-lg py-1.5 px-1">
          <p className="text-foreground-800 text-[11px] font-bold">{radio.activePrograms}</p>
          <p className="text-foreground-400 text-[7px] flex items-center justify-center gap-0.5 whitespace-nowrap">
            <i className="ri-calendar-check-line text-[7px]"></i>
            Progs
          </p>
        </div>
        <div className="text-center bg-background-100 rounded-lg py-1.5 px-1">
          <p className="text-foreground-800 text-[11px] font-bold">{formatNumber(radio.activeListeners)}</p>
          <p className="text-foreground-400 text-[7px] flex items-center justify-center gap-0.5 whitespace-nowrap">
            <i className="ri-user-line text-[7px]"></i>
            Oyentes
          </p>
        </div>
        <div className="text-center bg-background-100 rounded-lg py-1.5 px-1">
          <p className="text-foreground-800 text-[11px] font-bold">{formatNumber(radio.greetings)}</p>
          <p className="text-foreground-400 text-[7px] flex items-center justify-center gap-0.5 whitespace-nowrap">
            <i className="ri-chat-smile-2-line text-[7px]"></i>
            Saludos
          </p>
        </div>
        <div className="text-center bg-background-100 rounded-lg py-1.5 px-1">
          <p className="text-foreground-800 text-[11px] font-bold">{formatNumber(radio.songRequests)}</p>
          <p className="text-foreground-400 text-[7px] flex items-center justify-center gap-0.5 whitespace-nowrap">
            <i className="ri-music-line text-[7px]"></i>
            Canciones
          </p>
        </div>
        <div className="text-center bg-background-100 rounded-lg py-1.5 px-1">
          <p className="text-foreground-800 text-[11px] font-bold">{radio.activeChats}</p>
          <p className="text-foreground-400 text-[7px] flex items-center justify-center gap-0.5 whitespace-nowrap">
            <i className="ri-chat-3-line text-[7px]"></i>
            Chats
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between pt-1 border-t border-background-100">
        <span className="text-foreground-400 text-[9px] flex items-center gap-1">
          <i className="ri-time-line text-[9px]"></i>
          {radio.lastActivity}
        </span>
        <span className="text-foreground-500 text-[9px] font-medium">
          {formatNumber(radio.totalInteractions)} interacciones
        </span>
      </div>
    </div>
  );
}

/* ── Activity Distribution Card ── */
function ActivityDistributionCard() {
  const maxVal = Math.max(
    communityMetrics.totalGreetings,
    communityMetrics.totalSongRequests,
    communityMetrics.totalActiveChats,
  );

  const items = [
    { label: 'Saludos', value: communityMetrics.totalGreetings, icon: 'ri-chat-smile-2-line', color: 'bg-emerald-500', bg: 'bg-emerald-50', textColor: 'text-emerald-600' },
    { label: 'Canciones', value: communityMetrics.totalSongRequests, icon: 'ri-music-line', color: 'bg-violet-500', bg: 'bg-violet-50', textColor: 'text-violet-600' },
    { label: 'Chats', value: communityMetrics.totalActiveChats, icon: 'ri-chat-3-line', color: 'bg-accent-500', bg: 'bg-accent-50', textColor: 'text-accent-600' },
  ];

  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl p-5 space-y-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-accent-50 flex items-center justify-center">
          <i className="ri-bar-chart-grouped-line text-accent-500 text-lg"></i>
        </div>
        <div>
          <h3 className="text-foreground-800 text-sm font-bold">Distribución de Actividad Social</h3>
          <p className="text-foreground-400 text-[10px]">{formatNumber(communityMetrics.totalInteractions)} interacciones totales</p>
        </div>
      </div>

      <div className="space-y-3">
        {items.map((item) => (
          <div key={item.label} className="space-y-1.5">
            <div className="flex items-center justify-between text-[10px]">
              <span className="text-foreground-600 font-medium flex items-center gap-1.5">
                <span className={`w-2 h-2 rounded-full ${item.color}`}></span>
                {item.label}
              </span>
              <span className="text-foreground-800 font-bold">{formatNumber(item.value)}</span>
            </div>
            <div className="w-full h-3 bg-background-200 rounded-full overflow-hidden">
              <div
                className={`h-full ${item.color} rounded-full transition-all flex items-center justify-end pr-2`}
                style={{ width: `${Math.round((item.value / maxVal) * 100)}%` }}
              >
                <span className="text-background-50 text-[7px] font-bold">
                  {Math.round((item.value / communityMetrics.totalInteractions) * 100)}%
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-2 pt-1">
        <div className="bg-background-100 rounded-xl p-2.5 text-center">
          <p className="text-foreground-400 text-[8px]">Hora Pico</p>
          <p className="text-foreground-800 text-[10px] font-bold mt-0.5">{communityMetrics.peakActivityHour}</p>
        </div>
        <div className="bg-background-100 rounded-xl p-2.5 text-center">
          <p className="text-foreground-400 text-[8px]">Top Interacción</p>
          <p className="text-foreground-800 text-[10px] font-bold mt-0.5">{communityMetrics.topInteractionType}</p>
        </div>
        <div className="bg-background-100 rounded-xl p-2.5 text-center">
          <p className="text-foreground-400 text-[8px]">Ciudad Top</p>
          <p className="text-foreground-800 text-[10px] font-bold mt-0.5">{communityMetrics.mostActiveCity}</p>
        </div>
      </div>
    </div>
  );
}

/* ── Summary Bar ── */
function SummaryBar() {
  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl p-3.5 flex flex-wrap items-center gap-4">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
          <i className="ri-group-line text-emerald-500 text-sm"></i>
        </div>
        <div>
          <p className="text-foreground-400 text-[9px]">Radios Sociales</p>
          <p className="text-foreground-800 text-sm font-bold">{communityMetrics.totalActiveRadios}</p>
        </div>
      </div>
      <div className="w-px h-8 bg-background-200 hidden sm:block"></div>
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-sky-50 flex items-center justify-center">
          <i className="ri-user-voice-line text-sky-500 text-sm"></i>
        </div>
        <div>
          <p className="text-foreground-400 text-[9px]">Locutores</p>
          <p className="text-foreground-800 text-sm font-bold">{communityMetrics.totalActiveHosts}</p>
        </div>
      </div>
      <div className="w-px h-8 bg-background-200 hidden sm:block"></div>
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center">
          <i className="ri-calendar-event-line text-violet-500 text-sm"></i>
        </div>
        <div>
          <p className="text-foreground-400 text-[9px]">Programas</p>
          <p className="text-foreground-800 text-sm font-bold">{communityMetrics.totalActivePrograms}</p>
        </div>
      </div>
      <div className="w-px h-8 bg-background-200 hidden sm:block"></div>
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
          <i className="ri-flashlight-line text-amber-500 text-sm"></i>
        </div>
        <div>
          <p className="text-foreground-400 text-[9px]">Interacciones</p>
          <p className="text-foreground-800 text-sm font-bold">{formatNumber(communityMetrics.totalInteractions)}</p>
        </div>
      </div>
    </div>
  );
}

/* ── Loading Skeleton ── */
function LoadingSkeleton() {
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2">
        {Array.from({ length: 7 }).map((_, i) => (
          <div key={i} className="bg-background-50 border border-background-200 rounded-2xl p-4 flex items-center gap-3 animate-pulse">
            <div className="w-10 h-10 rounded-xl bg-background-200"></div>
            <div className="space-y-1">
              <div className="w-16 h-2.5 bg-background-200 rounded"></div>
              <div className="w-10 h-4 bg-background-200 rounded"></div>
            </div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-background-50 border border-background-200 rounded-2xl p-5 space-y-4 animate-pulse">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-background-200"></div>
            <div className="space-y-1">
              <div className="w-32 h-3.5 bg-background-200 rounded"></div>
              <div className="w-40 h-2.5 bg-background-200 rounded"></div>
            </div>
          </div>
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="space-y-1.5">
                <div className="w-20 h-2.5 bg-background-200 rounded"></div>
                <div className="w-full h-3 bg-background-200 rounded-full"></div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-12 bg-background-200 rounded-xl"></div>
            ))}
          </div>
        </div>
        <div className="bg-background-50 border border-background-200 rounded-2xl p-5 space-y-4 animate-pulse">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-background-200"></div>
            <div className="space-y-1">
              <div className="w-28 h-3.5 bg-background-200 rounded"></div>
              <div className="w-36 h-2.5 bg-background-200 rounded"></div>
            </div>
          </div>
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="space-y-1.5">
                <div className="w-20 h-2.5 bg-background-200 rounded"></div>
                <div className="w-full h-3 bg-background-200 rounded-full"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="bg-background-50 border border-background-200 rounded-2xl p-4 space-y-3 animate-pulse">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-background-200"></div>
              <div className="space-y-1 flex-1">
                <div className="w-3/4 h-3 bg-background-200 rounded"></div>
                <div className="w-1/2 h-2 bg-background-200 rounded"></div>
              </div>
            </div>
            <div className="h-2 bg-background-200 rounded-full"></div>
            <div className="grid grid-cols-6 gap-1.5">
              {Array.from({ length: 6 }).map((_, j) => (
                <div key={j} className="h-10 bg-background-200 rounded-lg"></div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Error State ── */
function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl p-12 text-center">
      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-red-50 flex items-center justify-center">
        <i className="ri-error-warning-line text-red-400 text-2xl"></i>
      </div>
      <p className="text-foreground-800 text-sm font-semibold mb-1">Error al cargar</p>
      <p className="text-foreground-400 text-xs mb-4">{message}</p>
      <button
        onClick={onRetry}
        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer"
      >
        <i className="ri-refresh-line text-sm"></i>
        Reintentar
      </button>
    </div>
  );
}

/* ── Empty State ── */
function EmptyState({ icon, title, subtitle }: { icon: string; title: string; subtitle: string }) {
  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl p-12 text-center">
      <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
        <i className={`${icon} text-foreground-300 text-xl`}></i>
      </div>
      <p className="text-foreground-500 text-sm font-medium">{title}</p>
      <p className="text-foreground-300 text-xs mt-1">{subtitle}</p>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════════════════════ */

export default function SACommunityCenter() {
  const [radios, setRadios] = useState<RadioCommunityStats[]>([]);
  const [loadingState, setLoadingState] = useState<LoadingState>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'score' | 'interactions' | 'listeners'>('score');

  const loadData = useCallback(() => {
    setLoadingState('loading');
    setErrorMsg(null);
    simulateLoad(radioCommunityStats)
      .then((data) => {
        setRadios(data);
        setLoadingState('ready');
      })
      .catch((err) => {
        setErrorMsg(err.message || 'Error desconocido');
        setLoadingState('error');
      });
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  /* Sorted & filtered radios */
  const sortedRadios = useMemo(() => {
    let result = [...radios];
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      result = result.filter(
        (r) =>
          r.radioName.toLowerCase().includes(q) ||
          r.radioCity.toLowerCase().includes(q) ||
          r.radioFrequency.toLowerCase().includes(q),
      );
    }
    if (sortBy === 'score') {
      result.sort((a, b) => b.communityScore - a.communityScore);
    } else if (sortBy === 'interactions') {
      result.sort((a, b) => b.totalInteractions - a.totalInteractions);
    } else {
      result.sort((a, b) => b.activeListeners - a.activeListeners);
    }
    return result;
  }, [radios, searchTerm, sortBy]);

  /* Computed metrics from loaded radios */
  const computedMetrics = useMemo(() => ({
    hosts: sortedRadios.reduce((s, r) => s + r.activeHosts, 0),
    programs: sortedRadios.reduce((s, r) => s + r.activePrograms, 0),
    listeners: sortedRadios.reduce((s, r) => s + r.activeListeners, 0),
    greetings: sortedRadios.reduce((s, r) => s + r.greetings, 0),
    songs: sortedRadios.reduce((s, r) => s + r.songRequests, 0),
    chats: sortedRadios.reduce((s, r) => s + r.activeChats, 0),
  }), [sortedRadios]);

  return (
    <div className="min-h-screen bg-background-100">
      <div className="flex">
        <SASidebar />
        <main className="flex-1 min-w-0">
          <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-5 max-w-[1600px] mx-auto">
            {/* Breadcrumbs */}
            <div className="flex items-center gap-1.5 text-[10px] text-foreground-400">
              <Link to="/sa/dashboard" className="hover:text-foreground-600 transition-colors cursor-pointer">Dashboard</Link>
              <i className="ri-arrow-right-s-line text-[9px]"></i>
              <span className="text-foreground-500 font-medium">Centro de Comunidades</span>
            </div>

            {/* Page header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-semibold mb-2">
                  <i className="ri-group-line text-xs"></i>
                  Bloque SA-16
                </div>
                <h2 className="text-foreground-950 font-bold text-lg">Centro de Comunidades</h2>
                <p className="text-foreground-400 text-xs mt-0.5">
                  Dashboard social · RFW es red social, no directorio · {loadingState === 'ready' ? `${radios.length} radios` : 'Cargando...'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {/* Sort selector */}
                <div className="flex items-center gap-2 px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs">
                  <span className="text-foreground-400 text-[10px] whitespace-nowrap">Ordenar:</span>
                  <button
                    onClick={() => setSortBy('score')}
                    className={`px-2 py-1 rounded-lg text-[10px] font-medium transition-all cursor-pointer whitespace-nowrap ${sortBy === 'score' ? 'bg-foreground-950 text-background-50' : 'text-foreground-500 hover:text-foreground-700'}`}
                  >
                    Score
                  </button>
                  <button
                    onClick={() => setSortBy('interactions')}
                    className={`px-2 py-1 rounded-lg text-[10px] font-medium transition-all cursor-pointer whitespace-nowrap ${sortBy === 'interactions' ? 'bg-foreground-950 text-background-50' : 'text-foreground-500 hover:text-foreground-700'}`}
                  >
                    Interacciones
                  </button>
                  <button
                    onClick={() => setSortBy('listeners')}
                    className={`px-2 py-1 rounded-lg text-[10px] font-medium transition-all cursor-pointer whitespace-nowrap ${sortBy === 'listeners' ? 'bg-foreground-950 text-background-50' : 'text-foreground-500 hover:text-foreground-700'}`}
                  >
                    Oyentes
                  </button>
                </div>
                <div className="relative w-full sm:w-52">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-xs"></i>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Buscar radio..."
                    disabled={loadingState !== 'ready'}
                    className="w-full pl-8 pr-8 py-2 bg-background-50 border border-background-200 rounded-xl text-xs text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors disabled:opacity-50"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 rounded-md bg-background-200 flex items-center justify-center cursor-pointer hover:bg-background-300 transition-colors"
                    >
                      <i className="ri-close-line text-foreground-500 text-[10px]"></i>
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Summary Bar */}
            {loadingState === 'ready' && <SummaryBar />}

            {/* KPIs */}
            {loadingState === 'ready' && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2">
                <KpiCard label="Radios Activas" value={String(sortedRadios.length)} icon="ri-radio-line" bg="bg-accent-50" color="text-accent-500" pulse />
                <KpiCard label="Locutores" value={String(computedMetrics.hosts)} icon="ri-mic-line" bg="bg-sky-50" color="text-sky-500" pulse />
                <KpiCard label="Programas" value={String(computedMetrics.programs)} icon="ri-calendar-check-line" bg="bg-violet-50" color="text-violet-500" pulse />
                <KpiCard label="Oyentes" value={formatNumber(computedMetrics.listeners)} icon="ri-user-line" bg="bg-emerald-50" color="text-emerald-500" />
                <KpiCard label="Saludos" value={formatNumber(computedMetrics.greetings)} icon="ri-chat-smile-2-line" bg="bg-amber-50" color="text-amber-500" />
                <KpiCard label="Canciones" value={formatNumber(computedMetrics.songs)} icon="ri-music-line" bg="bg-rose-50" color="text-rose-500" />
                <KpiCard label="Chats" value={String(computedMetrics.chats)} icon="ri-chat-3-line" bg="bg-primary-50" color="text-primary-500" pulse />
              </div>
            )}

            {/* Loading KPIs */}
            {loadingState === 'loading' && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2">
                {Array.from({ length: 7 }).map((_, i) => (
                  <div key={i} className="bg-background-50 border border-background-200 rounded-2xl p-4 flex items-center gap-3 animate-pulse">
                    <div className="w-10 h-10 rounded-xl bg-background-200"></div>
                    <div className="space-y-1">
                      <div className="w-16 h-2.5 bg-background-200 rounded"></div>
                      <div className="w-10 h-4 bg-background-200 rounded"></div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Distribution + Top Radio cards */}
            {loadingState === 'ready' && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2">
                  <ActivityDistributionCard />
                </div>
                <div className="bg-background-50 border border-background-200 rounded-2xl p-5 space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-accent-50 flex items-center justify-center">
                      <i className="ri-trophy-line text-accent-500 text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-foreground-800 text-sm font-bold">Top Community Score</h3>
                      <p className="text-foreground-400 text-[10px]">Promedio global: {communityMetrics.avgCommunityScore}/100</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {sortedRadios.slice(0, 5).map((radio, idx) => (
                      <div key={radio.id} className="flex items-center gap-3 p-2.5 rounded-xl bg-background-100 hover:bg-background-200 transition-colors">
                        <div className="w-6 h-6 rounded-md bg-background-200 flex items-center justify-center shrink-0">
                          <span className="text-foreground-500 text-[10px] font-bold">{idx + 1}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-foreground-800 text-[10px] font-semibold line-clamp-1">{radio.radioName}</p>
                          <p className="text-foreground-400 text-[8px]">{radio.radioFrequency}</p>
                        </div>
                        <ScoreGauge score={radio.communityScore} size="sm" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Section title */}
            {loadingState === 'ready' && (
              <div className="flex items-center gap-2 pt-1">
                <i className="ri-group-line text-foreground-400 text-sm"></i>
                <h3 className="text-foreground-800 text-sm font-bold">Radios · Actividad Comunitaria</h3>
                <span className="text-foreground-400 text-[10px]">{sortedRadios.length} radios</span>
              </div>
            )}

            {/* Radio Community Cards */}
            {loadingState === 'loading' && <LoadingSkeleton />}

            {loadingState === 'error' && <ErrorState message={errorMsg || 'Error'} onRetry={loadData} />}

            {loadingState === 'ready' && sortedRadios.length === 0 && (
              <EmptyState icon="ri-group-line" title="Sin resultados" subtitle="No hay radios que coincidan con la búsqueda" />
            )}

            {loadingState === 'ready' && sortedRadios.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {sortedRadios.map((radio, idx) => (
                  <RadioCommunityCard key={radio.id} radio={radio} rank={idx + 1} />
                ))}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Mobile bottom hint */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-background-50/98 backdrop-blur-2xl border-t border-background-200 py-3 px-4 safe-bottom">
        <p className="text-center text-foreground-400 text-[10px] font-medium">Super Admin · Centro de Comunidades</p>
      </div>
    </div>
  );
}